import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.ts";
import type { ParamUpdates } from "../../hooks/useUrlState.ts";
import { parseCommaSeparated } from "../../utils/urlFilters.ts";
import {
  type FilterSpec,
  type OptionsSpec,
  DEFAULT_DATE_PRESETS,
  presetFrom,
  specParams,
  typedParams,
} from "./specs.ts";

/** Where filter values live: the URL (`useUrlState`) or component state (`useLocalFilterState`). */
export interface FilterState {
  searchParams: URLSearchParams;
  /** Writes params (null/"" deletes) and resets pagination. */
  changeFilter: (updates: ParamUpdates, replace?: boolean) => void;
}

/** Filter values held in component state, for lists filtered in the browser. */
export function useLocalFilterState(): FilterState {
  const [searchParams, setParams] = useState(() => new URLSearchParams());
  const changeFilter = useCallback((updates: ParamUpdates) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        next.delete(key);
        if (Array.isArray(value)) for (const v of value) next.append(key, v);
        else if (value !== null && value !== "") next.set(key, value);
      }
      return next;
    });
  }, []);
  return { searchParams, changeFilter };
}

/** A page's filter params as stored in a saved view or the remembered filters. */
export type FilterParams = Record<string, string | string[]>;

interface UseFiltersOptions {
  state: FilterState;
  /** The page's main search field. */
  search?: { param: string; placeholder?: string };
  /** Names the page for remembered filters and saved views ("companies", "signals.inbox"). */
  pageKey?: string;
  /**
   * The tenant or org the filters belong to; remembered filters are kept per
   * scope. `undefined` while it is still loading (nothing is restored or saved
   * yet); `null` for a page without one.
   */
  scopeId?: string | null;
  /** Restore the last filters when the page opens without any in its URL. */
  remember?: boolean;
  /**
   * Params the page keeps in a preference of its own (Signals' countries):
   * left out of the remembered filters, so they neither stop a restore nor are
   * stored or overwritten by one. Saved views still hold them.
   */
  notRemembered?: string[];
}

const TYPING_DELAY = 400;
const REMEMBER_DELAY = 1000;

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

function isFilterParams(value: unknown): value is FilterParams {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (v) => typeof v === "string" || (Array.isArray(v) && v.every((x) => typeof x === "string")),
    )
  );
}

/** Key-order independent, so a restored set compares equal to the URL's. */
function canonical(params: FilterParams): string {
  return JSON.stringify(Object.keys(params).sort().map((k) => [k, params[k]]));
}

const compact = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });
function formatBound(raw: string): string {
  const n = Number(raw);
  return raw !== "" && Number.isFinite(n) ? compact.format(n) : raw;
}

function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}

/**
 * The state behind a `FilterBar`: each spec's value read from and written to
 * the URL (or local state), typed values that settle before they are written,
 * which pills show, the pill texts, "Clear all" back to the defaults, and the
 * page's remembered filters.
 */
export function useFilters(specs: FilterSpec[], options: UseFiltersOptions) {
  const { state, search, pageKey, scopeId, remember = false, notRemembered } = options;
  const { searchParams, changeFilter } = state;
  const get = useCallback((param: string) => searchParams.get(param), [searchParams]);

  // ---- typed fields: search, text and range inputs ----
  const typedFields = useMemo(
    () => [...(search ? [search.param] : []), ...specs.flatMap(typedParams)],
    [search, specs],
  );
  const typedKey = typedFields.join(",");
  const [typed, setTyped] = useState<Record<string, string>>(() =>
    Object.fromEntries(typedFields.map((f) => [f, searchParams.get(f) ?? ""])),
  );
  const settled = useDebouncedValue(typed, TYPING_DELAY);
  // What we last wrote to (or read from) the URL per typed field: a URL change
  // that isn't ours (back button, Clear all, a view) is copied into the input.
  const lastSeen = useRef<Record<string, string>>({ ...typed });

  useEffect(() => {
    const fromUrl: Record<string, string> = {};
    for (const f of typedKey.split(",").filter(Boolean)) {
      const url = searchParams.get(f) ?? "";
      if (url !== lastSeen.current[f]) {
        lastSeen.current[f] = url;
        fromUrl[f] = url;
      }
    }
    if (Object.keys(fromUrl).length) setTyped((prev) => ({ ...prev, ...fromUrl }));
  }, [searchParams, typedKey]);

  const changeRef = useRef(changeFilter);
  changeRef.current = changeFilter;
  useEffect(() => {
    const updates: ParamUpdates = {};
    for (const [f, v] of Object.entries(settled)) {
      if (v !== (lastSeen.current[f] ?? "")) {
        lastSeen.current[f] = v;
        updates[f] = v || null;
      }
    }
    if (Object.keys(updates).length) changeRef.current(updates, true);
  }, [settled]);

  const setText = useCallback((param: string, value: string) => {
    setTyped((prev) => (prev[param] === value ? prev : { ...prev, [param]: value }));
  }, []);
  const text = (param: string) => typed[param] ?? "";

  // ---- options ----
  const labelCache = useRef(new Map<string, string>());
  for (const spec of specs) {
    if (spec.kind === "options") for (const o of spec.options ?? []) labelCache.current.set(`${spec.key}:${o.value}`, o.label);
  }
  const rememberLabel = (spec: OptionsSpec, value: string, label: string) =>
    labelCache.current.set(`${spec.key}:${value}`, label);
  const optionLabel = (spec: OptionsSpec, value: string) =>
    labelCache.current.get(`${spec.key}:${value}`) ?? value;

  const defaultValues = (spec: OptionsSpec): string[] =>
    typeof spec.default === "function" ? spec.default(get) : (spec.default ?? []);

  const optionValues = (spec: OptionsSpec): string[] => {
    const raw =
      spec.encoding === "repeat" ? searchParams.getAll(spec.param) : parseCommaSeparated(get(spec.param));
    if (raw.length === 0) return defaultValues(spec);
    if (spec.noneValue && raw.length === 1 && raw[0] === spec.noneValue) return [];
    return raw;
  };

  const encodeOptions = (spec: OptionsSpec, values: string[]): string | string[] | null => {
    const def = defaultValues(spec);
    if (sameSet(values, def)) return null;
    if (values.length === 0) return def.length ? (spec.noneValue ?? null) : null;
    return spec.encoding === "repeat" ? values : values.join(",");
  };

  // ---- per spec ----
  const hasDefault = (spec: FilterSpec) => spec.kind === "options" && defaultValues(spec).length > 0;

  /** Differs from where "Clear all" would put it. */
  const isActive = (spec: FilterSpec): boolean => {
    switch (spec.kind) {
      case "options":
        return !sameSet(optionValues(spec), defaultValues(spec));
      case "custom":
        return spec.active ? spec.active(get) : spec.summary(get) !== null;
      default:
        return specParams(spec).some((p) => !!get(p));
    }
  };

  /** Has something to show, typed or applied. */
  const hasValue = (spec: FilterSpec): boolean =>
    isActive(spec) || typedParams(spec).some((p) => text(p) !== "");

  const summary = (spec: FilterSpec): string | null => {
    switch (spec.kind) {
      case "options": {
        const values = optionValues(spec);
        if (values.length === 0) return spec.noneValue && hasDefault(spec) ? (spec.noneLabel ?? "Any") : null;
        const first = optionLabel(spec, values[0]);
        return values.length > 1 ? `${first} +${values.length - 1}` : first;
      }
      case "boolean": {
        const v = get(spec.param);
        if (!v) return null;
        const yes = (spec.values?.yes ?? "yes") === v;
        return yes ? (spec.labels?.yes ?? "Yes") : (spec.labels?.no ?? "No");
      }
      case "range": {
        const min = text(spec.minParam);
        const max = spec.maxParam ? text(spec.maxParam) : "";
        if (!min && !max) return null;
        const unit = spec.unit ? ` ${spec.unit}` : "";
        if (min && max) return `${formatBound(min)}–${formatBound(max)}${unit}`;
        return min ? `≥ ${formatBound(min)}${unit}` : `≤ ${formatBound(max)}${unit}`;
      }
      case "dateRange": {
        const from = get(spec.fromParam) ?? "";
        const to = spec.toParam ? (get(spec.toParam) ?? "") : "";
        if (!from && !to) return null;
        if (from && !to) {
          const preset = (spec.presets ?? DEFAULT_DATE_PRESETS).find((p) => presetFrom(p) === from);
          if (preset) return preset.label;
          return `Since ${formatDay(from)}`;
        }
        if (!from) return `Until ${formatDay(to)}`;
        return `${formatDay(from)} – ${formatDay(to)}`;
      }
      case "text":
        return text(spec.param) || null;
      case "custom":
        return spec.summary(get);
    }
  };

  /** Back to empty (or the default). `keepPill`: the pill stays, e.g. "Clear" inside its open popover. */
  const clear = (spec: FilterSpec, { keepPill = false }: { keepPill?: boolean } = {}) => {
    for (const p of typedParams(spec)) {
      lastSeen.current[p] = "";
      setText(p, "");
    }
    changeFilter(Object.fromEntries(specParams(spec).map((p) => [p, null])));
    if (keepPill) return;
    setAdded((prev) => {
      const next = new Set(prev);
      next.delete(spec.key);
      return next;
    });
  };

  // ---- which pills show ----
  // A filter that had a value this session stays as a pill until removed, so
  // clearing a value doesn't make the pill jump away under the pointer.
  const [added, setAdded] = useState<Set<string>>(new Set());
  const withValue = specs.filter(hasValue).map((s) => s.key).join(",");
  useEffect(() => {
    if (!withValue) return;
    setAdded((prev) => {
      const keys = withValue.split(",");
      return keys.every((k) => prev.has(k)) ? prev : new Set([...prev, ...keys]);
    });
  }, [withValue]);

  const isVisible = (spec: FilterSpec) =>
    !!spec.pinned || hasDefault(spec) || added.has(spec.key) || hasValue(spec);
  const add = (key: string) => setAdded((prev) => new Set(prev).add(key));
  /** A popover closed: an added filter left empty goes back into "+ Filter". */
  const release = (key: string) => {
    const spec = specs.find((s) => s.key === key);
    if (!spec || spec.pinned || hasValue(spec)) return;
    setAdded((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // ---- whole bar ----
  const ownParams = useMemo(
    () => [...(search ? [search.param] : []), ...specs.flatMap(specParams)],
    [search, specs],
  );

  const params: FilterParams = {};
  for (const p of ownParams) {
    const all = searchParams.getAll(p);
    if (all.length > 1) params[p] = all;
    else if (all.length === 1 && all[0] !== "") params[p] = all[0];
  }

  const applyParams = useCallback(
    (next: FilterParams, replace = false, only: string[] = ownParams) => {
      // Typed inputs take the new values at once, so a pending keystroke
      // can't settle on top of them afterwards.
      const typedValues: Record<string, string> = {};
      for (const f of typedFields) {
        if (!only.includes(f)) continue;
        const v = next[f];
        typedValues[f] = typeof v === "string" ? v : (v?.[0] ?? "");
        lastSeen.current[f] = typedValues[f];
      }
      setTyped((prev) => ({ ...prev, ...typedValues }));
      changeFilter(Object.fromEntries(only.map((p) => [p, next[p] ?? null])), replace);
    },
    [changeFilter, ownParams, typedFields],
  );

  const clearAll = () => {
    applyParams({});
    setAdded(new Set());
  };

  const activeCount = specs.filter(isActive).length + (search && get(search.param) ? 1 : 0);

  // ---- remembered filters ----
  const notRememberedKey = (notRemembered ?? []).join(",");
  const rememberedParams = useMemo(
    () => ownParams.filter((p) => !notRememberedKey.split(",").includes(p)),
    [ownParams, notRememberedKey],
  );
  const toKeep: FilterParams = {};
  for (const p of rememberedParams) if (p in params) toKeep[p] = params[p];
  const keepKey = canonical(toKeep);
  const { user, updateUiPreferences } = useAuth();
  const scopeReady = scopeId !== undefined;
  const prefKey = pageKey && scopeReady ? `filters.${pageKey}.${scopeId ?? "all"}` : null;
  const stored = prefKey ? user?.ui_preferences?.[prefKey] : undefined;
  const [restored, setRestored] = useState<string | null>(null);
  const restoredSeen = useRef(false);
  // The scope last restored (a tenant switch keeps the page mounted but is a
  // new scope), and a restore still on its way to the URL.
  const restoredFor = useRef<string | null>(null);
  const pendingRestore = useRef<string | null>(null);

  useEffect(() => {
    if (!remember || !user || !prefKey || restoredFor.current === prefKey) return;
    restoredFor.current = prefKey;
    if (Object.keys(toKeep).length === 0 && isFilterParams(stored) && Object.keys(stored).length > 0) {
      const key = canonical(stored);
      pendingRestore.current = key;
      restoredSeen.current = false;
      setRestored(key);
      applyParams(stored, true, rememberedParams);
    }
    // Once per scope, when the page has a user and its scope loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, prefKey]);

  // The hint goes once the (committed) filters move away from what was restored.
  useEffect(() => {
    if (restored === null) return;
    if (keepKey === restored) restoredSeen.current = true;
    else if (restoredSeen.current) {
      restoredSeen.current = false;
      setRestored(null);
    }
  }, [keepKey, restored]);

  const toRemember = useDebouncedValue(keepKey, REMEMBER_DELAY);
  const paramsRef = useRef(toKeep);
  paramsRef.current = toKeep;
  useEffect(() => {
    if (!remember || !prefKey || restoredFor.current !== prefKey) return;
    // A restore must reach the URL, and the debounce catch up with it, before
    // anything is saved; a stale debounced value (another scope's) never is.
    if (pendingRestore.current !== null) {
      if (toRemember !== pendingRestore.current) return;
      pendingRestore.current = null;
    }
    if (toRemember !== keepKey) return;
    const current = isFilterParams(stored) ? canonical(stored) : canonical({});
    if (toRemember === current) return;
    const next = paramsRef.current;
    updateUiPreferences({ [prefKey]: Object.keys(next).length ? next : null }).catch(() => {});
    // Only when the filters settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toRemember, prefKey, keepKey]);

  return {
    specs,
    search: search && {
      ...search,
      value: text(search.param),
      set: (v: string) => setText(search.param, v),
    },
    pageKey,
    scopeId,
    get,
    getAll: (param: string) => searchParams.getAll(param),
    /** Raw writes, for custom specs. */
    update: (updates: Record<string, string | null>) => changeFilter(updates),
    text,
    setText,
    optionValues,
    setOptionValues: (spec: OptionsSpec, values: string[]) =>
      changeFilter({ [spec.param]: encodeOptions(spec, values) }),
    rememberLabel,
    setBoolean: (spec: Extract<FilterSpec, { kind: "boolean" }>, value: "yes" | "no" | null) =>
      changeFilter({
        [spec.param]: value === null ? null : value === "yes" ? (spec.values?.yes ?? "yes") : (spec.values?.no ?? "no"),
      }),
    booleanValue: (spec: Extract<FilterSpec, { kind: "boolean" }>): "yes" | "no" | null => {
      const v = get(spec.param);
      if (!v) return null;
      return v === (spec.values?.yes ?? "yes") ? "yes" : "no";
    },
    isActive,
    isDefault: (spec: FilterSpec) => hasDefault(spec) && !isActive(spec),
    hasDefault,
    hasValue,
    summary,
    clear,
    visible: specs.filter(isVisible),
    // A filter only links set (a custom one without an editor) is never offered in "+ Filter".
    hidden: specs.filter((s) => !isVisible(s) && (s.kind !== "custom" || !!s.renderEditor)),
    add,
    release,
    activeCount,
    clearAll,
    params,
    applyParams,
    restored: restored !== null,
    dismissRestored: () => setRestored(null),
  };
}

export type Filters = ReturnType<typeof useFilters>;
